import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";

import getSearchResults from "@/api/requests/getSearchResults";
import getShopResults from "@/api/requests/getShopResults";
import { Query } from "@/custom-types/Query";
import {
  BrandsShallow,
  DispensariesShallow,
  MappedFilterOption,
  StrainsShallow,
} from "@/custom-types/Shop";
import { useSelector } from "@/redux/hooks";
import { getCoordinates } from "@/redux/selectors/location";
import generateShopQuery from "@/utils/generateShopQuery";

import useDomainCountryCode from "./useDomainCountryCode";
import useShopSearchParams from "./useShopSearchParams";

export const MAX_DISTANCE = 30;

export const isCategoryHub = (query: Query) => {
  const { product_category, q, shopCategory, strain_category, strain_name } =
    query;

  return (
    !!product_category &&
    !q &&
    !shopCategory &&
    !strain_category &&
    !strain_name
  );
};

const useShop = () => {
  const { push } = useRouter();

  const {
    deal_active,
    product_category,
    q,
    shopCategory,
    shopName,
    showSimilar,
    strain_category,
    strain_name,
  } = useShopSearchParams();

  const countryCode = useDomainCountryCode();
  const coordinates = useSelector(getCoordinates);

  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState<
    BrandsShallow | StrainsShallow | undefined
  >();
  const [dispensaries, setDispensaries] = useState<DispensariesShallow[]>([]);
  const [filters, setFilters] = useState<MappedFilterOption[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  useEffect(() => {
    let isMounted = true;

    const getResults = async () => {
      setLoading(true);

      try {
        const selectionQuery = generateShopQuery({
          shopCategory,
          shopName,
          type: shopCategory,
        });

        const shopResultsQuery = generateShopQuery({
          deal_active,
          medical: "false",
          product_category,
          q,
          shopCategory,
          shopName,
          strain_category,
          strain_name,
          type: "menu_item",
        });

        const isSelection =
          (shopCategory === "brand" || shopCategory === "strain") && !!shopName;

        const [selectionResults, shopResults] = await Promise.all([
          isSelection
            ? getSearchResults(
                selectionQuery,
                coordinates,
                MAX_DISTANCE,
                countryCode,
                1,
                0,
                "",
                true,
              )
            : undefined,
          getShopResults(
            shopResultsQuery,
            coordinates,
            MAX_DISTANCE,
            countryCode,
            shopCategory === "strain" &&
              (showSimilar === "on" || showSimilar === "menu_items_only"),
          ),
        ]);

        if (!isMounted) {
          return;
        }

        const selectionResult =
          selectionResults?.[shopCategory as "brand" | "strain"]?.[0];

        if (
          shopCategory === "brand" &&
          selectionResult?.slug &&
          shopResults.totalCount === 0
        ) {
          push(`/brands/${selectionResult.slug}`);
          return;
        }

        if (shopCategory === "brand") {
          setSelection(selectionResult as BrandsShallow | undefined);
        } else if (shopCategory === "strain") {
          setSelection(selectionResult as StrainsShallow | undefined);
        } else {
          setSelection(undefined);
        }

        setDispensaries(shopResults.dispensary);

        const mappedFilters: MappedFilterOption[] =
          shopResults.availableFilters.map((filter) => ({
            label: filter.label,
            name: filter.name,
            values: filter.values.map((v) => String(v)),
          }));

        setFilters(mappedFilters);
        setTotalCount(shopResults.totalCount || 0);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    getResults();

    return () => {
      isMounted = false;
    };
  }, [
    deal_active,
    product_category,
    q,
    shopCategory,
    shopName,
    showSimilar,
    strain_category,
    strain_name,
    JSON.stringify(coordinates),
  ]);

  return {
    dispensaries,
    filters,
    loading,
    selection,
    showEmptyState: !totalCount && !loading,
    totalCount,
  };
};

export default useShop;
